import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Icon, IconButton } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VideocamIcon from "@mui/icons-material/Videocam";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import isImage from "utils/images/isImage";
import getExtensionFilename from "utils/strings/getExtensionFromFilename";

export enum AttachmentKind {
  Image,
  Pdf,
  Video,
  File,
}

export interface GalleryFile {
  name?: string;
  path?: string | null;
  href?: string;
}

export const getAttachmentKind = (filename?: string) => {
  if (!filename) {
    return AttachmentKind.File;
  }

  const normalized = filename.toLowerCase();

  if (isImage(normalized)) {
    return AttachmentKind.Image;
  }

  if (normalized.endsWith(".pdf")) {
    return AttachmentKind.Pdf;
  }

  if (/\.(mp4|mov|avi|wmv|webm|m4v)$/i.test(normalized)) {
    return AttachmentKind.Video;
  }

  return AttachmentKind.File;
};

export const buildPreviewUrl = (path?: string | null) =>
  path ? `${import.meta.env.VITE_CDN_BASE_URL}/${path}` : "";

const getAttachmentIcon = (kind: AttachmentKind) => {
  switch (kind) {
    case AttachmentKind.Pdf:
      return PictureAsPdfIcon;
    case AttachmentKind.Video:
      return VideocamIcon;
    default:
      return InsertDriveFileIcon;
  }
};

const getAttachmentIconColor = (kind: AttachmentKind) => {
  switch (kind) {
    case AttachmentKind.Pdf:
      return "#d32f2f";
    case AttachmentKind.Video:
      return "#1976d2";
    default:
      return "#344767";
  }
};

const getAttachmentBackground = (kind: AttachmentKind) => {
  switch (kind) {
    case AttachmentKind.Image:
      return "#dbe5f0";
    case AttachmentKind.Pdf:
      return "linear-gradient(180deg, #fff5f5 0%, #fee2e2 100%)";
    case AttachmentKind.Video:
      return "linear-gradient(180deg, #f0f9ff 0%, #dbeafe 100%)";
    default:
      return "linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)";
  }
};

export function ProjectFileGalleryCard({
  file,
  kind,
  selected = false,
  selectable = false,
  size = "clamp(140px, 18vw, 220px)",
  footerLabel,
  footerMeta,
  onPrimaryClick,
  onDelete,
}: {
  file: GalleryFile;
  kind?: AttachmentKind;
  selected?: boolean;
  selectable?: boolean;
  size?: string;
  footerLabel?: string;
  footerMeta?: string;
  onPrimaryClick?: () => void;
  onDelete?: () => void;
}) {
  const resolvedKind = kind ?? getAttachmentKind(file.name || file.path || "");
  const previewUrl = file.href || buildPreviewUrl(file.path);
  const extension = getExtensionFilename(file.name || "")?.toUpperCase();
  const IconComponent = getAttachmentIcon(resolvedKind);

  return (
    <MDBox
      sx={{
        width: size,
        borderRadius: "1rem",
        overflow: "hidden",
        border: selected ? "2px solid" : "1px solid #dfe5ef",
        borderColor: selected ? "success.main" : undefined,
        boxShadow: selected
          ? "0 0 0 4px rgba(76, 175, 80, 0.12)"
          : "0 10px 24px rgba(15, 23, 42, 0.06)",
        transition: "all 180ms ease",
        position: "relative",
        bgcolor: "white",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 18px 38px rgba(15, 23, 42, 0.14)",
        },
      }}
    >
      {selectable && (
        <MDBox
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 1,
            width: 24,
            height: 24,
            borderRadius: "999px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: selected ? "success.main" : "rgba(255,255,255,0.92)",
            color: selected ? "#fff" : "#7b8794",
            border: selected ? "none" : "1px solid #d6dbe5",
          }}
        >
          <Icon sx={{ fontSize: 16 }}>{selected ? "check" : "add"}</Icon>
        </MDBox>
      )}

      {onDelete && !selectable && (
        <MDBox
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 1,
            background: "rgba(255, 255, 255, 0.92)",
            borderRadius: "999px",
            boxShadow: "0 4px 14px rgba(15, 23, 42, 0.18)",
          }}
        >
          <IconButton size="small" onClick={onDelete}>
            <Icon fontSize="small" color="error">
              delete
            </Icon>
          </IconButton>
        </MDBox>
      )}

      <MDBox
        onClick={onPrimaryClick}
        sx={{
          cursor: onPrimaryClick ? "pointer" : "default",
          height: 156,
          bgcolor: getAttachmentBackground(resolvedKind),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderBottom: "1px solid #edf1f7",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {resolvedKind === AttachmentKind.Image && previewUrl ? (
          <>
            <MDBox
              component="img"
              src={previewUrl}
              alt={file.name || "Project file"}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transform: "scale(1.02)",
              }}
            />
            <MDBox
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(to top, rgba(15,23,42,0.22), rgba(15,23,42,0) 45%)",
              }}
            />
          </>
        ) : (
          <MDBox
            textAlign="center"
            color="#5b667a"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={0.75}
          >
            <MDBox
              sx={{
                width: 64,
                height: 64,
                borderRadius: "1rem",
                bgcolor: "#fff",
                border: "1px solid #e1e7f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 18px rgba(15, 23, 42, 0.08)",
              }}
            >
              <IconComponent sx={{ width: "52%", height: "52%", color: getAttachmentIconColor(resolvedKind) }} />
            </MDBox>
            <MDTypography variant="caption" fontWeight="bold" letterSpacing="0.08em">
              {extension || "FILE"}
            </MDTypography>
          </MDBox>
        )}
      </MDBox>

      <MDBox p={1.5}>
        <MDTypography
          variant="button"
          fontWeight="medium"
          sx={{
            display: "-webkit-box",
            overflow: "hidden",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            minHeight: "2.6em",
          }}
        >
          {footerLabel || file.name}
        </MDTypography>
        {footerMeta && (
          <MDTypography variant="caption" color="text">
            {footerMeta}
          </MDTypography>
        )}
      </MDBox>
    </MDBox>
  );
}
